import { useState } from 'react';

interface ShareButtonProps {
  /** Base share message. Tournament hashtags are appended automatically. */
  text: string;
  /** Tournament-specific hashtags (e.g. ["#BigTenWrestling", "#NCAAWrestling"]) */
  hashtags?: string[];
  /** URL to share alongside the message (default: https://takedownleague.com) */
  url?: string;
  /** Label for the primary share button (default: "Share") */
  label?: string;
  className?: string;
}

const SHARE_URL = 'https://takedownleague.com';

/**
 * Builds the full share text from the base message and optional hashtags.
 * Hashtags are appended after the message body.
 */
function buildShareText(text: string, hashtags: string[]): string {
  if (hashtags.length === 0) return text;
  return `${text} ${hashtags.join(' ')}`;
}

/**
 * Reusable share component.
 * - Mobile: single button using Web Share API (native share sheet).
 * - Desktop: X/Twitter, Facebook, and Copy-to-clipboard buttons.
 */
export function ShareButton({
  text,
  hashtags = [],
  url = SHARE_URL,
  label = 'Share',
  className = '',
}: ShareButtonProps) {
  const [copied, setCopied] = useState(false);

  const shareText = buildShareText(text, hashtags);
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const handleNativeShare = async () => {
    try {
      await navigator.share({ text: shareText, url });
    } catch {
      // User cancelled or browser blocked — silently ignore
    }
  };

  const handleCopy = () => {
    // Copy includes URL since there's no separate URL field for clipboard
    navigator.clipboard.writeText(`${shareText}\n\n${url}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;

  if (canNativeShare) {
    return (
      <button
        onClick={handleNativeShare}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg text-sm font-medium hover:shadow-lg transition-shadow ${className}`}
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
          />
        </svg>
        {label}
      </button>
    );
  }

  // Desktop: platform buttons
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      {/* X / Twitter */}
      <a
        href={twitterUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-black text-white rounded-lg text-xs font-medium hover:bg-gray-800 transition-colors"
        title="Share on X / Twitter"
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.742l7.732-8.865L2.25 2.25h6.922l4.255 5.626L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
        Post
      </a>

      {/* Facebook */}
      <a
        href={facebookUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-[#1877F2] text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors"
        title="Share on Facebook"
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
        Share
      </a>

      {/* Copy to clipboard */}
      <button
        onClick={handleCopy}
        className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-300 transition-colors"
        title="Copy to clipboard"
      >
        {copied ? (
          <>
            <svg
              className="w-3.5 h-3.5 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="text-green-600">Copied!</span>
          </>
        ) : (
          <>
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            Copy
          </>
        )}
      </button>
    </div>
  );
}
